import { LOGO_URL, DPIIT } from "../data/mock";
import { useContent } from "../hooks/useContent";
import { Button } from "./ui/button";
import { Zap, ArrowRight, Star, Clock, Truck, ShieldCheck, Award } from "lucide-react";

export const HeroSection = () => {
  const { getSection } = useContent();
  const heroData = getSection("hero") || {};
  const stats = getSection("stats") || [];

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-sky-50/80 via-white to-lime-50/30 pt-24 lg:pt-0">
      <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-sky-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-lime-100/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" />
      <div className="absolute top-1/3 left-1/4 w-2 h-2 bg-sky-400 rounded-full animate-pulse" />
      <div className="absolute top-1/2 right-1/3 w-3 h-3 bg-lime-400/60 rounded-full animate-pulse" style={{ animationDelay: "1s" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 lg:py-0">
          <div className="max-w-xl animate-fadeInUp">
            <div className="flex flex-wrap items-center gap-2 mb-6 mt-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-50 border border-sky-200">
                <Zap className="w-4 h-4 text-lime-500" />
                <span className="text-sm font-semibold text-sky-700">
                  {heroData.badgeText}
                </span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-orange-50 border border-orange-200" title={DPIIT.fullLabel} data-testid="hero-dpiit-badge">
                <Award className="w-3.5 h-3.5 text-orange-500" />
                <span className="text-xs font-bold text-orange-700">DPIIT Recognized</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.1] tracking-tight mb-6">
              Fresh Clothes.
              <br />
              <span className="relative">
                Delivered in{" "}
                <span className="text-sky-600 relative">
                  24 Hours
                  <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8" fill="none">
                    <path d="M1 5.5C47 2 153 2 199 5.5" stroke="#84CC16" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </span>
                .
              </span>
            </h1>

            <p className="text-lg text-slate-500 leading-relaxed mb-10 max-w-lg">
              {heroData.subheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button
                size="lg"
                className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-base font-semibold shadow-xl shadow-sky-600/20 hover:shadow-sky-600/30 transition-all duration-300 rounded-xl group"
                onClick={() => scrollTo("#contact")}
              >
                {heroData.ctaPrimary}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 px-8 py-6 text-base font-semibold rounded-xl transition-all duration-300"
                onClick={() => scrollTo("#pricing")}
              >
                {heroData.ctaSecondary}
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {stats.map((stat, i) => (
                <div key={i} className="text-left">
                  <div className="flex items-center gap-1">
                    <span className="text-2xl font-bold text-slate-900">{stat.value}</span>
                    {stat.isStar && <Star className="w-5 h-5 text-lime-500 fill-lime-500" />}
                  </div>
                  <span className="text-sm text-slate-400 font-medium">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block animate-fadeInUp" style={{ animationDelay: "0.2s" }}>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">
                <img
                  src="/images/gen/hero_1.png"
                  alt="Freshly cleaned Indian silk sarees stacked on wooden shelf"
                  className="w-full h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
              </div>

              <div className="absolute -left-8 top-16 bg-white rounded-2xl shadow-xl shadow-slate-900/10 p-4 flex items-center gap-3 animate-float border border-sky-100">
                <div className="w-11 h-11 rounded-xl bg-lime-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-lime-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">24 Hour</p>
                  <p className="text-xs text-slate-400">Delivery</p>
                </div>
              </div>

              <div className="absolute -right-6 top-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl shadow-slate-900/10 p-4 flex items-center gap-3 animate-float border border-sky-100" style={{ animationDelay: "1s" }}>
                <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">Free Pickup</p>
                  <p className="text-xs text-slate-400">& Delivery</p>
                </div>
              </div>

              <div className="absolute -left-4 bottom-20 bg-white rounded-2xl shadow-xl shadow-slate-900/10 p-4 flex items-center gap-3 animate-float border border-sky-100" style={{ animationDelay: "2s" }}>
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">Premium</p>
                  <p className="text-xs text-slate-400">Quality Care</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
